import { supabase } from '@/lib/supabase';
import type { ApiResponse } from '@/types';

/**
 * 구매자 계정 삭제
 * 삭제 대상 테이블:
 * - users (기본 사용자 정보) - CASCADE로 연관 데이터 자동 삭제
 * - buyer_profiles (구매자 프로필)
 * - user_addresses (사용자 주소)
 * - favorites (즐겨찾기)
 * - user_coupons (사용자 쿠폰)
 * - user_points (포인트 잔액)
 * - carts (장바구니)
 * - cart_items (장바구니 아이템)
 * - notifications (개인 알림)
 * 
 * 보존 테이블 (정산/분쟁 대비):
 * - orders (주문 정보)
 * - order_items (주문 아이템)
 * - payments (결제 정보)
 * - reviews (리뷰는 익명화 처리)
 */
export async function deleteBuyerAccount(userId: string): Promise<ApiResponse<null>> {
  try {
    // 트랜잭션으로 처리
    const { error } = await supabase.rpc('delete_buyer_account', {
      user_id: userId
    });

    if (error) {
      console.error('Failed to delete buyer account:', error);
      return {
        success: false,
        error: '계정 삭제에 실패했습니다.'
      };
    }

    // Supabase Auth에서도 사용자 삭제
    try {
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      if (authError) {
        console.error('Auth user deletion failed:', authError);
        // DB는 이미 삭제되었으므로 경고만 로그
      }
    } catch (authError) {
      console.warn('Auth user deletion failed:', authError);
    }

    return {
      success: true,
      message: '계정이 성공적으로 삭제되었습니다.'
    };
  } catch (error) {
    console.error('Error deleting buyer account:', error);
    return {
      success: false,
      error: '계정 삭제 중 오류가 발생했습니다.'
    };
  }
}

/**
 * 판매자 계정 삭제
 * 삭제 대상 테이블:
 * - users (기본 사용자 정보) - CASCADE로 연관 데이터 자동 삭제
 * - sellers (판매자 정보)
 * - restaurants (매장 정보) - CASCADE로 연관 데이터 자동 삭제
 * - products (상품 정보)
 * - product_offers (상품 오퍼)
 * - product_stock (재고 정보)
 * - restaurant_images (매장 이미지)
 * - restaurant_operating_hours (영업시간)
 * - restaurant_service_schedules (서비스 스케줄)
 * 
 * 보존 테이블 (정산/분쟁 대비):
 * - orders (주문 정보)
 * - order_items (주문 아이템)
 * - payments (결제 정보)
 * - settlements (정산 정보)
 */
export async function deleteSellerAccount(userId: string): Promise<ApiResponse<null>> {
  try {
    // 진행 중인 주문 확인
    const { data: activeOrders, error: orderCheckError } = await supabase
      .from('orders')
      .select('id')
      .eq('restaurant_id', userId)
      .or('status.eq.PENDING,status.eq.ACCEPTED,status.eq.READY');

    if (orderCheckError) {
      console.error('Failed to check active orders:', orderCheckError);
      return {
        success: false,
        error: '주문 상태 확인 중 오류가 발생했습니다.'
      };
    }

    if (activeOrders && activeOrders.length > 0) {
      return {
        success: false,
        error: '진행 중인 주문이 있어 계정을 삭제할 수 없습니다. 모든 주문을 완료한 후 다시 시도해주세요.'
      };
    }

    // 트랜잭션으로 처리
    const { error } = await supabase.rpc('delete_seller_account', {
      user_id: userId
    });

    if (error) {
      console.error('Failed to delete seller account:', error);
      return {
        success: false,
        error: '계정 삭제에 실패했습니다.'
      };
    }

    // Supabase Auth에서도 사용자 삭제
    try {
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      if (authError) {
        console.error('Auth user deletion failed:', authError);
        // DB는 이미 삭제되었으므로 경고만 로그
      }
    } catch (authError) {
      console.warn('Auth user deletion failed:', authError);
    }

    return {
      success: true,
      message: '계정이 성공적으로 삭제되었습니다.'
    };
  } catch (error) {
    console.error('Error deleting seller account:', error);
    return {
      success: false,
      error: '계정 삭제 중 오류가 발생했습니다.'
    };
  }
}

/**
 * 사용자 리뷰 익명화 처리
 * 리뷰는 삭제하지 않고 사용자 정보만 제거
 */
export async function anonymizeUserReviews(userId: string): Promise<ApiResponse<null>> {
  try {
    const { error } = await supabase
      .from('reviews')
      .update({ 
        user_id: null,
        is_anonymous: true
      })
      .eq('user_id', userId);

    if (error) {
      console.error('Failed to anonymize reviews:', error);
      return {
        success: false,
        error: '리뷰 익명화 처리에 실패했습니다.'
      };
    }

    return {
      success: true,
      message: '리뷰가 익명화 처리되었습니다.'
    };
  } catch (error) {
    console.error('Error anonymizing reviews:', error);
    return {
      success: false,
      error: '리뷰 익명화 처리 중 오류가 발생했습니다.'
    };
  }
}

/**
 * 계정 삭제 전 사전 검증
 * - 진행 중인 주문 확인
 * - 정산 대기 상태 확인
 * - 기타 삭제 불가 조건 확인
 */
export async function validateAccountDeletion(userId: string, userRole: 'buyer' | 'seller'): Promise<ApiResponse<{ canDelete: boolean; reason?: string }>> {
  try {
    if (userRole === 'buyer') {
      // 구매자는 주문 확인 없이 바로 탈퇴 가능
      return {
        success: true,
        data: { canDelete: true }
      };
    } else if (userRole === 'seller') {
      // 판매자의 경우 진행 중인 주문과 정산 상태 확인
      const { data: restaurantData, error: restaurantError } = await supabase
        .from('restaurants')
        .select('id')
        .eq('owner_id', userId)
        .single();

      if (restaurantError && restaurantError.code !== 'PGRST116') {
        return {
          success: false,
          error: '매장 정보 확인 중 오류가 발생했습니다.'
        };
      }

      if (restaurantData) {
        const { data: activeOrders, error: orderError } = await supabase
          .from('orders')
          .select('id, status')
          .eq('restaurant_id', restaurantData.id)
          .or('status.eq.PENDING,status.eq.ACCEPTED,status.eq.READY');

        if (orderError) {
          return {
            success: false,
            error: '주문 상태 확인 중 오류가 발생했습니다.'
          };
        }

        if (activeOrders && activeOrders.length > 0) {
          return {
            success: true,
            data: {
              canDelete: false,
              reason: `진행 중인 주문이 ${activeOrders.length}건 있습니다. 모든 주문을 완료한 후 다시 시도해주세요.`
            }
          };
        }

      }
    }

    return {
      success: true,
      data: { canDelete: true }
    };
  } catch (error) {
    console.error('Error validating account deletion:', error);
    return {
      success: false,
      error: '계정 삭제 가능 여부 확인 중 오류가 발생했습니다.'
    };
  }
}